// ============================================================
// NOVO ARQUIVO → frontend/src/components/pinwin/LoginPage.jsx
// ============================================================
import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { API } from "@/lib/assets";

const fade = (d = 0) => ({ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: d } });

function Shell({ children }) {
  return (
    <motion.div {...fade(0)} className="text-center max-w-sm w-full">
      {children}
    </motion.div>
  );
}

function ForgotPasswordForm() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    setErrorMsg("");
    try {
      const r = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await r.json();
      if (!r.ok || !data.ok) {
        setStatus("error");
        setErrorMsg(data?.error?.message || "Something went wrong. Please try again.");
        return;
      }
      setStatus("sent");
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please try again.");
    }
  };

  if (status === "sent") {
    return (
      <Shell>
        <p className="text-5xl mb-4">📬</p>
        <h1 className="font-anton text-2xl text-white uppercase mb-2">Check your email</h1>
        <p className="font-body text-sm text-white/50 mb-8">
          If an account exists with that email, we've sent a link to reset your password. It expires in 1 hour.
        </p>
        <Link to="/" className="inline-block font-mono-pw text-sm tracking-widest text-black bg-[#E6FF00] px-8 py-3 uppercase">
          Back to PINWIN
        </Link>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="font-anton text-3xl text-white uppercase mb-3">Forgot your password?</h1>
      <p className="font-body text-sm text-white/50 mb-8">
        Enter the email on your PINWIN account and we'll send you a reset link.
      </p>
      <form onSubmit={submit} className="text-left">
        <label className="font-mono-pw text-[10px] text-white/40 tracking-widest uppercase block mb-2">
          Your email
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          required
          className="w-full bg-white/5 border border-white/10 text-white text-sm px-4 py-3 mb-4 outline-none focus:border-[#E6FF00]/50"
        />
        {status === "error" && <p className="text-red-400 text-xs mb-4">{errorMsg}</p>}
        <button
          type="submit"
          disabled={status === "loading"}
          className="w-full font-mono-pw text-sm tracking-widest text-black bg-[#E6FF00] px-8 py-4 uppercase hover:brightness-110 transition-all disabled:opacity-50"
        >
          {status === "loading" ? "Sending..." : "Send reset link"}
        </button>
      </form>
    </Shell>
  );
}

function ResetPasswordForm({ token }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setStatus("error");
      setErrorMsg("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      setStatus("error");
      setErrorMsg("Passwords don't match");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      const r = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await r.json();
      if (!r.ok || !data.ok) {
        setStatus("error");
        setErrorMsg(data?.error?.message || "This link is invalid or has expired");
        return;
      }
      setStatus("done");
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please try again.");
    }
  };

  if (status === "done") {
    return (
      <Shell>
        <p className="text-5xl mb-4">✅</p>
        <h1 className="font-anton text-2xl text-white uppercase mb-2">Password updated</h1>
        <p className="font-body text-sm text-white/50 mb-8">Open PINWIN and log in with your new password.</p>
        <a
          href="pinwin://"
          className="block font-mono-pw text-sm tracking-widest text-black bg-[#E6FF00] px-8 py-4 uppercase text-center hover:brightness-110 transition-all"
        >
          Open PINWIN
        </a>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="font-anton text-3xl text-white uppercase mb-3">Set a new password</h1>
      <p className="font-body text-sm text-white/50 mb-8">Minimum 6 characters.</p>
      <form onSubmit={submit} className="text-left">
        <label className="font-mono-pw text-[10px] text-white/40 tracking-widest uppercase block mb-2">
          New password
        </label>
        <div className="relative mb-4">
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full bg-white/5 border border-white/10 text-white text-sm px-4 py-3 pr-16 outline-none focus:border-[#E6FF00]/50"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 text-[10px] font-mono-pw uppercase tracking-widest"
          >
            {showPassword ? "Hide" : "Show"}
          </button>
        </div>
        <label className="font-mono-pw text-[10px] text-white/40 tracking-widest uppercase block mb-2">
          Confirm password
        </label>
        <input
          type={showPassword ? "text" : "password"}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
          className="w-full bg-white/5 border border-white/10 text-white text-sm px-4 py-3 mb-4 outline-none focus:border-[#E6FF00]/50"
        />
        {status === "error" && <p className="text-red-400 text-xs mb-4">{errorMsg}</p>}
        <button
          type="submit"
          disabled={status === "loading"}
          className="w-full font-mono-pw text-sm tracking-widest text-black bg-[#E6FF00] px-8 py-4 uppercase hover:brightness-110 transition-all disabled:opacity-50"
        >
          {status === "loading" ? "Updating..." : "Update password"}
        </button>
      </form>
    </Shell>
  );
}

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center px-6 py-16">
      {token ? <ResetPasswordForm token={token} /> : <ForgotPasswordForm />}
    </div>
  );
}
